import React from "react";
import Image from "next/image";
import Link from "next/link";
import type { Blocks } from "@/types/cms";
import { optimizeCloudinaryUrl } from "@/lib/videoConfig";
import { highlightFeatureDne } from "@/lib/featureDne";

// URLs that start with "/" (and aren't protocol-relative "//...") are treated
// as internal so they use Next's client-side router instead of full-page
// loads. Prevents cross-tab navigation that breaks the first-visit
// `PageLoader` gate.
const isInternalUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  const trimmed = url.trim();
  return trimmed.startsWith("/") && !trimmed.startsWith("//");
};

// Guards link hrefs against XSS-bearing schemes (javascript:, data:,
// vbscript:). Allows relative URLs ("/" or "#") and absolute
// http:/https:/mailto:. Anything else is rejected so we render plain text
// instead of an interactive link.
const isSafeUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) return true;
  const lowered = trimmed.toLowerCase();
  return (
    lowered.startsWith("http:") ||
    lowered.startsWith("https:") ||
    lowered.startsWith("mailto:")
  );
};

// Legacy "Blocks" rich-text nodes are heterogeneous and loosely typed (the
// `Blocks` alias is `any[]`). This single structural type captures every
// property the renderers below read off a block or inline leaf, so the
// walking code stays type-checked without resorting to `any`.
type RichNode = {
  type?: string;
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  strikethrough?: boolean;
  url?: string;
  level?: number;
  textAlign?: string;
  format?: string;
  src?: string;
  alt?: string;
  width?: number;
  height?: number;
  header?: boolean;
  colSpan?: number;
  rowSpan?: number;
  children?: RichNode[];
  items?: RichNode[];
};

export function getFirstHeadingFromBlocks(
  blocks: Blocks | undefined
): string | undefined {
  if (!Array.isArray(blocks)) return undefined;
  for (const block of blocks as RichNode[]) {
    if (block?.type === "heading" && Array.isArray(block.children)) {
      const text = block.children
        .map((child: RichNode) =>
          typeof child.text === "string" ? child.text : ""
        )
        .join("")
        .trim();
      if (text) return text;
    }
  }
  return undefined;
}

export function getFirstParagraphFromBlocks(
  blocks: Blocks | undefined
): string | undefined {
  if (!Array.isArray(blocks)) return undefined;
  for (const block of blocks as RichNode[]) {
    if (block?.type === "paragraph" && Array.isArray(block.children)) {
      const text = block.children
        .map((child: RichNode) =>
          typeof child.text === "string" ? child.text : ""
        )
        .join("")
        .trim();
      if (text) return text;
    }
  }
  return undefined;
}

function getAlignmentClass(textAlign?: string): string {
  const val = (textAlign || "").toLowerCase();
  if (val === "center") return "align-center";
  if (val === "right") return "align-right";
  if (val === "justify") return "align-justify";
  return "align-left";
}

type RichTextAlign = "left" | "center" | "right" | "justify";

function renderInlineLeaves(nodes: RichNode[], accentClass?: string): React.ReactNode {
  if (!Array.isArray(nodes)) return null;
  return nodes.map((child, i) => {
    if (!child || typeof child !== "object") return null;

    // Run every CMS-driven leaf through the FEATURE DNE highlighter
    // before any text-decoration wrapping. Marker survives bold/italic/
    // underline/code/strikethrough and link transforms because each one
    // wraps `content` further down the chain.
    let content: React.ReactNode = highlightFeatureDne(child.text ?? "");

    if (child.bold) content = <strong>{content}</strong>;
    if (child.italic) {
      content = accentClass ? (
        <span className={accentClass}>{content}</span>
      ) : (
        <em>{content}</em>
      );
    }
    if (child.underline) content = <u>{content}</u>;
    if (child.code) {
      // Inline-code formatting: monospace pill with subtle bg.
      content = (
        <code
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.92em",
            padding: "0.1em 0.32em",
            background: "color-mix(in oklab, var(--foreground) 8%, transparent)",
            borderRadius: "4px",
          }}
        >
          {content}
        </code>
      );
    }
    if (child.strikethrough) content = <s>{content}</s>;

    if (child.type === "link" && child.url) {
      const linkText =
        (Array.isArray(child.children)
          ? child.children.map((c: RichNode) => c.text ?? "").join("")
          : child.text) || "";
      content = !isSafeUrl(child.url) ? (
        // Unsafe scheme (javascript:/data:/vbscript:) — drop the href and
        // render the link text as plain inline content.
        <span style={{ whiteSpace: "pre-wrap" }}>{linkText}</span>
      ) : isInternalUrl(child.url) ? (
        <Link href={child.url} style={{ whiteSpace: "pre-wrap" }}>
          {linkText}
        </Link>
      ) : (
        <a
          href={child.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {linkText}
        </a>
      );
    }

    const className = child.strikethrough ? "muted" : undefined;

    return (
      <span
        key={i}
        className={className}
        style={{ whiteSpace: "pre-wrap" }}
      >
        {content}
      </span>
    );
  });
}

export function renderRichTextWithMutedStrikethrough(
  blocks: Blocks | undefined,
  options?: {
    defaultAlign?: RichTextAlign;
    headingVariant?: "title" | "accent";
    /** Override the typography class applied to paragraphs. Defaults to
     *  `body-md`. Use cases: section subtitles want `title-lg` instead. */
    paragraphClass?: string;
    /** When true (default), paragraphs ship the global `muted` class so
     *  body copy renders at muted-foreground. Pass `false` for surfaces
     *  like the TextSection prose body where copy should be foreground. */
    mutedParagraphs?: boolean;
  }
): React.ReactNode {
  if (!Array.isArray(blocks)) return null;

  const asArray = blocks as RichNode[];

  return asArray.map((block, index) => {
    if (!block) return null;

    const children = Array.isArray(block.children) ? block.children : [];
    const isLast = index === asArray.length - 1;

    const renderInline = (nodes: RichNode[], accentClass?: string) =>
      renderInlineLeaves(nodes, accentClass);

    switch (block.type) {
      case "heading": {
        const headingAlign = getAlignmentClass(
          (block.textAlign as string | undefined) ?? options?.defaultAlign
        );
        const level = block.level || 2;
        const clamped = Math.min(Math.max(level, 1), 6);
        const TitleTag = (["h1", "h2", "h3", "h4", "h5", "h6"] as const)[
          clamped - 1
        ];
        const titleClass =
          clamped === 1
            ? "title-2xl"
            : clamped === 2
            ? "title-xl"
            : clamped === 3
            ? "title-lg"
            : clamped === 4
            ? "title-md"
            : clamped === 5
            ? "title-sm"
            : "title-xs";
        const accentClass =
          clamped === 1
            ? "accent-3xl"
            : clamped === 2
            ? "accent-2xl"
            : clamped === 3
            ? "accent-xl"
            : clamped === 4
            ? "accent-lg"
            : clamped === 5
            ? "accent-md"
            : "accent-sm";

        const headingClass =
          options?.headingVariant === "accent" ? accentClass : titleClass;

        const marginBottom = isLast
          ? 0
          : clamped <= 2
          ? 16
          : clamped <= 4
          ? 12
          : 8;

        return (
          <TitleTag
            key={index}
            className={`${headingClass} ${headingAlign}`}
            style={{ marginBottom, whiteSpace: "pre-wrap" }}
          >
            {renderInline(children, accentClass)}
          </TitleTag>
        );
      }
      case "paragraph": {
        const paragraphAlign = getAlignmentClass(
          (block.textAlign as string | undefined) ?? options?.defaultAlign
        );
        const paragraphMargin = isLast ? 0 : 16;
        // Detect a fully empty paragraph (Lexical emits one when the
        // editor presses Enter on an empty line). We render NBSP so the
        // line still occupies vertical space — without this, the
        // paragraph collapses to height 0 and the "blank line" the
        // editor saw is lost on the frontend.
        const isEmpty =
          !children.length ||
          children.every(
            (c: RichNode) =>
              (typeof c?.text !== "string" || c.text === "") &&
              c?.type !== "link" &&
              !(Array.isArray(c?.children) && c.children.length)
          );
        const pClass = options?.paragraphClass ?? "body-md";
        const mutedClass = options?.mutedParagraphs === false ? "" : "muted";
        return (
          <p
            key={index}
            className={`${pClass} ${mutedClass} ${paragraphAlign}`.trim()}
            style={{ marginBottom: paragraphMargin, whiteSpace: "pre-wrap" }}
          >
            {isEmpty ? " " : renderInline(children)}
          </p>
        );
      }
      case "quote":
      case "blockquote": {
        const quoteAlign = getAlignmentClass(
          (block.textAlign as string | undefined) ?? options?.defaultAlign
        );
        const quoteMargin = isLast ? 0 : 16;
        return (
          <blockquote
            key={index}
            className={`body-md ${quoteAlign}`}
            style={{
              marginBottom: quoteMargin,
              paddingLeft: 16,
              borderLeft: "3px solid var(--border)",
              color: "var(--muted-foreground)",
              fontStyle: "italic",
              whiteSpace: "pre-wrap",
            }}
          >
            {renderInline(children)}
          </blockquote>
        );
      }
      case "code":
      case "code-block": {
        // Lexical's code block stores its text in a single child of type
        // 'text' (sometimes flattened by lexicalToBlocks into one
        // children entry). Render as <pre><code> with monospace styling.
        const codeText = Array.isArray(children)
          ? children
              .map((c: RichNode) =>
                typeof c?.text === "string"
                  ? c.text
                  : Array.isArray(c?.children)
                    ? c.children.map((cc: RichNode) => cc?.text ?? "").join("")
                    : ""
              )
              .join("")
          : "";
        return (
          <pre
            key={index}
            style={{
              marginBottom: isLast ? 0 : 16,
              padding: "12px 16px",
              background:
                "color-mix(in oklab, var(--foreground) 6%, transparent)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              overflowX: "auto",
              whiteSpace: "pre",
              fontFamily: "var(--font-mono)",
              fontSize: "0.92em",
              lineHeight: 1.5,
            }}
          >
            <code style={{ fontFamily: "inherit" }}>{codeText}</code>
          </pre>
        );
      }
      case "list":
      case "bullet-list":
      case "ordered-list": {
        const isOrdered =
          block.format === "ordered" || block.type === "ordered-list";
        const ListTag = isOrdered ? "ol" : "ul";
        const listItems = block.children || block.items || [];
        return (
          <ListTag key={index} style={{ marginBottom: isLast ? 0 : 16 }}>
            {Array.isArray(listItems)
              ? listItems.map((item: RichNode, liIndex: number) => (
                  <li
                    key={liIndex}
                    className={options?.paragraphClass ?? "body-md"}
                  >
                    {renderInline(item.children || [])}
                  </li>
                ))
              : null}
          </ListTag>
        );
      }
      case "horizontal-rule":
      case "horizontalrule":
      case "hr":
      case "thematic-break":
      case "thematicbreak":
        return (
          <hr
            key={index}
            style={{
              border: 0,
              borderTop: "1px solid var(--border, rgba(0,0,0,0.15))",
              margin: isLast ? "0" : "24px 0",
            }}
          />
        );
      case "image":
      case "upload": {
        // Editor-inserted images from the Lexical upload node. The legacy
        // block carries the raw Cloudinary URL plus the real intrinsic
        // dimensions; we run the URL through the project-wide
        // `optimizeCloudinaryUrl` so the served bytes match the image quality
        // preset, and hand width/height to <Image> so Next emits a srcset
        // and picks the right variant per breakpoint. The `sizes` attribute
        // matches the `.prose` 720px max-width so phones load a much smaller
        // bucket than desktops.
        const src = block?.src;
        if (!src) return null;
        const optimized = optimizeCloudinaryUrl(src, false) || src;
        const intrinsicWidth =
          typeof block?.width === "number" ? block.width : 1200;
        const intrinsicHeight =
          typeof block?.height === "number"
            ? block.height
            : Math.round(intrinsicWidth * (2 / 3));
        const alt = typeof block?.alt === "string" ? block.alt : "";
        return (
          <figure
            key={index}
            style={{ margin: isLast ? 0 : "16px 0" }}
          >
            <Image
              src={optimized}
              alt={alt}
              width={intrinsicWidth}
              height={intrinsicHeight}
              sizes="(max-width: 768px) 100vw, 720px"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                borderRadius: "8px",
              }}
            />
          </figure>
        );
      }
      case "table": {
        // Lexical table (EXPERIMENTAL_TableFeature) via lexicalToBlocks:
        // table > table-row > table-cell, where each cell's children are
        // full blocks (usually a single paragraph) — rendered through a
        // recursive call so inline marks/links/lists inside cells keep
        // working. Cells force `mutedParagraphs: false`: table copy (and
        // especially header cells) should read at foreground even on
        // surfaces whose prose defaults to muted. The wrapper div scrolls
        // horizontally so wide tables never stretch the page body.
        const cellStyle: React.CSSProperties = {
          border: "1px solid var(--border)",
          padding: "8px 12px",
          textAlign: "left",
          verticalAlign: "top",
        };
        return (
          <div
            key={index}
            style={{ overflowX: "auto", marginBottom: isLast ? 0 : 16 }}
          >
            <table
              style={{ borderCollapse: "collapse", width: "100%" }}
            >
              <tbody>
                {children
                  .filter((row) => row?.type === "table-row")
                  .map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {(row.children || [])
                        .filter((cell) => cell?.type === "table-cell")
                        .map((cell, cellIndex) => {
                          const CellTag = cell.header ? "th" : "td";
                          return (
                            <CellTag
                              key={cellIndex}
                              scope={cell.header ? "col" : undefined}
                              colSpan={cell.colSpan}
                              rowSpan={cell.rowSpan}
                              style={
                                cell.header
                                  ? {
                                      ...cellStyle,
                                      fontWeight: 600,
                                      background:
                                        "color-mix(in oklab, var(--foreground) 6%, transparent)",
                                    }
                                  : cellStyle
                              }
                            >
                              {renderRichTextWithMutedStrikethrough(
                                cell.children,
                                {
                                  ...options,
                                  mutedParagraphs: false,
                                  // Tables read left by default even inside
                                  // centered sections; explicit per-paragraph
                                  // alignment from the editor still wins.
                                  defaultAlign: "left",
                                }
                              )}
                            </CellTag>
                          );
                        })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        );
      }
      default:
        // Fallback: render children inline in a paragraph
        return (
          <p
            key={index}
            className="body-md align-left"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {renderInline(children)}
          </p>
        );
    }
  });
}

const LIST_TYPES = new Set(["list", "bullet-list", "ordered-list"]);

type PriceFeature = { title: RichNode[]; subtitles: RichNode[][]; key: string };

function pairAsTitleSubtitle(items: PriceFeature[]): PriceFeature[] {
  const paired: PriceFeature[] = [];
  for (let i = 0; i < items.length; i += 2) {
    const titleItem = items[i];
    const subtitleItem = items[i + 1];
    if (subtitleItem) {
      paired.push({
        title: titleItem.title,
        subtitles: [subtitleItem.title],
        key: titleItem.key,
      });
    } else {
      paired.push(titleItem);
    }
  }
  return paired;
}

export function renderPriceFeatures(
  blocks: Blocks | undefined,
  classNames: {
    feature: string;
    featureTitle: string;
    featureSubtitle: string;
    featureGroup: string;
  }
): React.ReactNode {
  if (!Array.isArray(blocks)) return null;

  const getListItems = (listBlock: RichNode): RichNode[] => {
    const items = listBlock?.children ?? listBlock?.items;
    return Array.isArray(items) ? items : [];
  };

  const output: React.ReactNode[] = [];

  (blocks as RichNode[]).forEach((block, blockIndex) => {
    if (!block) return;

    if (LIST_TYPES.has(block.type ?? "")) {
      // legacy CMS Blocks represents nested bullets as sibling `list` children
      // (with indentLevel > 0) following a `list-item`, rather than as true
      // children of the list-item. Walk siblings and group each list-item with
      // the indented list(s) that follow it.
      const childrenArr: RichNode[] = Array.isArray(block.children)
        ? block.children
        : [];

      type Feature = { title: RichNode[]; subtitles: RichNode[][]; key: string };
      const features: Feature[] = [];
      let current: Feature | null = null;

      const attachNestedList = (nestedList: RichNode, feature: Feature | null) => {
        if (!feature) return;
        getListItems(nestedList).forEach((ni: RichNode) => {
          const leaves = Array.isArray(ni?.children)
            ? ni.children.filter((c: RichNode) => c && !LIST_TYPES.has(c.type ?? ""))
            : [];
          feature.subtitles.push(leaves);
        });
      };

      for (let i = 0; i < childrenArr.length; i++) {
        const child = childrenArr[i];
        if (!child) continue;

        if (child.type === "list-item") {
          if (current) features.push(current);
          const liChildren: RichNode[] = Array.isArray(child.children)
            ? child.children
            : [];
          const titleLeaves = liChildren.filter(
            (c: RichNode) => c && !LIST_TYPES.has(c.type ?? "")
          );
          const nestedInside = liChildren.filter(
            (c: RichNode) => c && LIST_TYPES.has(c.type ?? "")
          );
          current = {
            title: titleLeaves,
            subtitles: [],
            key: `${blockIndex}-${i}`,
          };
          nestedInside.forEach((nl: RichNode) => attachNestedList(nl, current));
        } else if (LIST_TYPES.has(child.type ?? "")) {
          attachNestedList(child, current);
        }
      }
      if (current) features.push(current);

      // Pair flat items as title → subtitle when the editor didn't produce any
      // nested lists (Lexical writes every list-item as a flat sibling, so this
      // is the common case). Keeps the "bold feature + muted description"
      // layout without requiring authors to manually indent.
      const noNestedLists = features.every((f) => f.subtitles.length === 0);
      const displayFeatures = noNestedLists && features.length >= 2
        ? pairAsTitleSubtitle(features)
        : features;

      output.push(
        <div key={`g-${blockIndex}`} className={classNames.featureGroup}>
          {displayFeatures.map((f) => (
            <div key={f.key} className={classNames.feature}>
              <div className={`${classNames.featureTitle} title-sm`}>
                {renderInlineLeaves(f.title)}
              </div>
              {f.subtitles.map((sub, si) => (
                <div
                  key={si}
                  className={`${classNames.featureSubtitle} body-xs`}
                >
                  {renderInlineLeaves(sub)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
      return;
    }

    if (block.type === "paragraph") {
      const children = Array.isArray(block.children) ? block.children : [];
      output.push(
        <p
          key={`p-${blockIndex}`}
          className="body-sm"
          style={{ whiteSpace: "pre-wrap", color: "var(--foreground)" }}
        >
          {renderInlineLeaves(children)}
        </p>
      );
      return;
    }

    // Fallback for any other block types (headings, etc.).
    output.push(
      <React.Fragment key={`b-${blockIndex}`}>
        {renderRichTextWithMutedStrikethrough([block])}
      </React.Fragment>
    );
  });

  return output;
}

/**
 * Render the body of a price card: paragraphs as `body-sm` sans-serif, the
 * next heading as `title-sm`, lists as plain sans-serif bullets. Walks
 * blocks in document order — no special title/subtitle pairing — so the
 * editor's authored sequence is what shows under the CTA.
 */
export function renderPriceBody(
  blocks: Blocks | undefined,
  classNames: {
    paragraph?: string;
    heading?: string;
    bulletList?: string;
    bulletItem?: string;
  } = {}
): React.ReactNode {
  if (!Array.isArray(blocks)) return null;

  const out: React.ReactNode[] = [];
  (blocks as RichNode[]).forEach((block, i) => {
    if (!block) return;

    if (block.type === "paragraph") {
      const children = Array.isArray(block.children) ? block.children : [];
      out.push(
        <p
          key={`pp-${i}`}
          className={`body-sm ${classNames.paragraph ?? ""}`.trim()}
          style={{ whiteSpace: "pre-wrap", color: "var(--foreground)" }}
        >
          {renderInlineLeaves(children)}
        </p>
      );
      return;
    }

    if (block.type === "heading") {
      const children = Array.isArray(block.children) ? block.children : [];
      out.push(
        <div
          key={`ph-${i}`}
          className={`title-xs ${classNames.heading ?? ""}`.trim()}
        >
          {renderInlineLeaves(children)}
        </div>
      );
      return;
    }

    if (LIST_TYPES.has(block.type ?? "")) {
      const items = Array.isArray(block.children)
        ? block.children.filter((c: RichNode) => c?.type === "list-item")
        : [];
      const Tag = block.type === "ordered-list" ? "ol" : "ul";
      out.push(
        <Tag
          key={`pl-${i}`}
          className={classNames.bulletList}
        >
          {items.map((item: RichNode, ii: number) => {
            const itemChildren = Array.isArray(item.children)
              ? item.children.filter((c: RichNode) => !LIST_TYPES.has(c?.type ?? ""))
              : [];
            return (
              <li
                key={ii}
                className={`body-sm ${classNames.bulletItem ?? ""}`.trim()}
              >
                {renderInlineLeaves(itemChildren)}
              </li>
            );
          })}
        </Tag>
      );
      return;
    }
  });

  return out;
}

/**
 * Parse one line of the price card's `valueProps` markdown into leaf nodes for
 * `renderInlineLeaves`. Handles only the documented subset — `**bold**` and
 * `[text](url)` — so we don't ship a full markdown engine (react-markdown +
 * unified + micromark) for one CMS field. Anything else is plain text.
 */
function parseValuePropsInline(text: string): Array<Record<string, unknown>> {
  const leaves: Array<Record<string, unknown>> = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) leaves.push({ text: text.slice(lastIndex, m.index) });
    if (m[1] !== undefined) {
      leaves.push({ text: m[1], bold: true });
    } else if (m[2] !== undefined) {
      leaves.push({ type: "link", url: m[3], text: m[2] });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) leaves.push({ text: text.slice(lastIndex) });
  return leaves.length ? leaves : [{ text }];
}

/**
 * Render the price card's `valueProps` markdown string. Drop-in replacement for
 * the previous `<ReactMarkdown>` usage in cardPrice.tsx — same output markup
 * (`<ul className={bulletList}>` of plain `<li>`, other lines as
 * `<p className={paragraph}>`). Consecutive `-`/`*` lines group into one list;
 * inline `**bold**` / `[links]` go through the shared `renderInlineLeaves` (so
 * feature-DNE highlighting + internal/external link handling stay consistent).
 * Unknown markdown renders as plain paragraphs rather than throwing.
 */
export function renderValuePropsMarkdown(
  md: string | undefined,
  classNames: { bulletList?: string; paragraph?: string } = {}
): React.ReactNode {
  if (typeof md !== "string" || !md.trim()) return null;

  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: React.ReactNode[] = [];
  let bullets: string[] = [];
  let key = 0;

  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    bullets = [];
    out.push(
      <ul key={`vp-ul-${key++}`} className={classNames.bulletList}>
        {items.map((item, i) => (
          <li key={i}>{renderInlineLeaves(parseValuePropsInline(item))}</li>
        ))}
      </ul>
    );
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      continue;
    }
    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      continue;
    }
    flushBullets();
    out.push(
      <p key={`vp-p-${key++}`} className={classNames.paragraph}>
        {renderInlineLeaves(parseValuePropsInline(line))}
      </p>
    );
  }
  flushBullets();

  return out;
}
